import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';

import { EngineInformation, OperatorSession, Site, Workstation } from '../api';

import { ApplicationState } from './ApplicationState';

const initialState: ApplicationState = {
  signalrConnected: false,
  site: undefined,
  operatorSession: undefined,
  workstation: undefined,
  engineInfo: undefined,
  workstationInitialised: false,
  serverLastPolled: 'Never',
  menuDataChanged: 0,
  stateLoadErrors: [],
  layoutDefinitionVersionLastPolled: 0,
  packageVersionLastPolled: 0,
  menuVersionLastPolled: 0,
  pendingDispenseCount: 0,
  readyToServeCount: 0,
  outstandingDispenseCount: 0,
  overdueOrderDevices: [],
} as ApplicationState;

const appStateSlice = createSlice({
  name: 'appState',
  initialState,
  reducers: {
    setWorkstation: (state: Draft<ApplicationState>, action: PayloadAction<Workstation>) => {
      state.workstation = action.payload;
    },
    setOperatorSession: (state: Draft<ApplicationState>, action: PayloadAction<OperatorSession>) => {
      state.operatorSession = action.payload;
    },
    clearOperatorSession: (state: Draft<ApplicationState>) => {
      state.operatorSession = undefined;
    },
    setSignalrStatus: (state: Draft<ApplicationState>, action: PayloadAction<boolean>) => {
      state.signalrConnected = action.payload;
    },
    setSite: (state: Draft<ApplicationState>, action: PayloadAction<Site>) => {
      state.site = action.payload;
    },
    setEngineInfo: (state: Draft<ApplicationState>, action: PayloadAction<EngineInformation>) => {
      state.engineInfo = action.payload;
    },
    setServerLastPolled: (state: Draft<ApplicationState>, action: PayloadAction<string>) => {
      state.serverLastPolled = action.payload;
    },
    setPollData: (
      state: Draft<ApplicationState>,
      action: PayloadAction<{ date: string; layoutDefinitionVersion: number; menuVersion: number; packageVersion: number }>,
    ) => {
      state.serverLastPolled = action.payload.date;
      state.layoutDefinitionVersionLastPolled = action.payload.layoutDefinitionVersion;
      state.packageVersionLastPolled = action.payload.packageVersion;
      state.menuVersionLastPolled = action.payload.menuVersion;
    },
    setMenuDataChanged: (state: Draft<ApplicationState>) => {
      state.menuDataChanged = new Date().getTime();
    },
    addLoadError: (state: Draft<ApplicationState>, action: PayloadAction<string>) => {
      state.stateLoadErrors.push(action.payload);
    },
    setTrainingModeSource: (state: Draft<ApplicationState>, action: PayloadAction<string>) => {
      state.trainingModeSourceUrl = action.payload;
    },
    setWorkstationBlocked: (state: Draft<ApplicationState>, action: PayloadAction<boolean>) => {
      if (state.workstation) state.workstation.isSessionBlockNewTransactions = action.payload;
    },
    setWorkstationClosed: (state: Draft<ApplicationState>, action: PayloadAction<boolean>) => {
      if (state.workstation) state.workstation.hasSession = action.payload;
    },
    setPendingDispenseCount: (state: Draft<ApplicationState>, action: PayloadAction<number>) => {
      state.pendingDispenseCount = action.payload;
    },
    incrementPendingDispenseCount: (state: Draft<ApplicationState>) => {
      state.pendingDispenseCount += 1;
    },
    decrementPendingDispenseCount: (state: Draft<ApplicationState>) => {
      state.pendingDispenseCount = Math.max(0, state.pendingDispenseCount - 1);
    },
    setReadyToServeCount: (state: Draft<ApplicationState>, action: PayloadAction<number>) => {
      state.readyToServeCount = action.payload;
    },
    incrementReadyToServeCount: (state: Draft<ApplicationState>) => {
      state.readyToServeCount += 1;
    },
    decrementReadyToServeCount: (state: Draft<ApplicationState>) => {
      state.readyToServeCount = Math.max(0, state.readyToServeCount - 1);
    },
    setOutstandingDispenseCount: (state: Draft<ApplicationState>, action: PayloadAction<number>) => {
      state.outstandingDispenseCount = action.payload;
    },
    incrementOutstandingDispenseCount: (state: Draft<ApplicationState>) => {
      state.outstandingDispenseCount += 1;
    },
    decrementOutstandingDispenseCount: (state: Draft<ApplicationState>) => {
      state.outstandingDispenseCount = Math.max(0, state.outstandingDispenseCount - 1);
    },
    addOverdueOrderDevice: (state: Draft<ApplicationState>, action: PayloadAction<number>) => {
      if (!state.overdueOrderDevices.includes(action.payload)) state.overdueOrderDevices.push(action.payload);
    },
    removeOverdueOrderDevice: (state: Draft<ApplicationState>, action: PayloadAction<number>) => {
      state.overdueOrderDevices = state.overdueOrderDevices.filter((id) => id !== action.payload);
    },
    clearOverdueOrderDevices: (state: Draft<ApplicationState>) => {
      state.overdueOrderDevices = [];
    },
  },
});
export const {
  setWorkstation,
  clearOperatorSession,
  setOperatorSession,
  setSignalrStatus,
  setSite,
  setEngineInfo,
  setServerLastPolled,
  setMenuDataChanged,
  setTrainingModeSource,
  addLoadError,
  setWorkstationBlocked,
  setWorkstationClosed,
  setPollData,
  setPendingDispenseCount,
  incrementPendingDispenseCount,
  decrementPendingDispenseCount,
  setReadyToServeCount,
  incrementReadyToServeCount,
  decrementReadyToServeCount,
  setOutstandingDispenseCount,
  incrementOutstandingDispenseCount,
  decrementOutstandingDispenseCount,
  addOverdueOrderDevice,
  removeOverdueOrderDevice,
  clearOverdueOrderDevices,
} = appStateSlice.actions;

export default appStateSlice.reducer;
